import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { InsuredList } from '../components/InsuredList';
import api from '../lib/api';
import { toast } from 'sonner';

// Helper functions para conversión de fechas
const convertDDMMYYYYtoYYYYMMDD = (dateStr) => {
  if (!dateStr || dateStr === '') return '';
  
  // Si ya está en formato YYYY-MM-DD, devolverlo tal cual
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Si está en formato DD/MM/AAAA, convertir a YYYY-MM-DD
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  return dateStr;
};

const convertYYYYMMDDtoDDMMYYYY = (dateStr) => {
  if (!dateStr || dateStr === '') return '';
  
  // Si ya está en formato DD/MM/AAAA, devolverlo tal cual
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Si está en formato YYYY-MM-DD, convertir a DD/MM/AAAA
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }
  
  return dateStr;
};

export const PolicyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [sameAsInsured, setSameAsInsured] = useState(false);  // Checkbox para copiar datos
  const [isPrefilled, setIsPrefilled] = useState(false)
  const [catalogs, setCatalogs] = useState({
    insurers: [],
    policy_types: [],
    currencies: [],
    payment_frequencies: [],
    payment_methods: [],
    statuses: []
  });
  // NOTA: zipCodeData fue eliminado ya que no se usa dirección en pólizas
  const [availablePlans, setAvailablePlans] = useState([]);
  const [formData, setFormData] = useState({
    policy_number: '',
    insurer: '',
    emission_date: '',
    end_date: '',
    contractor: { name: '', dob: '' },  // Contratante con nombre y fecha de nacimiento
    main_insured: { name: '', dob: '' },  // Asegurado Principal con nombre y fecha de nacimiento
    additional_insured: [],
    plan: '',
    policy_type: '',
    status: '',
    currency: '',
    insured_amount: '',
    annual_premium: '',
    // NOTA: phone, email, address y alias fueron eliminados - se obtienen del cliente vinculado
    payment: {
      frequency: '',
      method: '',
      medium: '',
    },
    vehicle_info: {
      vehiculo_asegurado: '',
      modelo: '',
      serie: '',
      placa: '',
    },
  });

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    // Manejar pre-carga de datos desde cliente
    const prefillData = location.state?.prefillFromClient;
    
    // Solo ejecutar si hay datos de precarga Y aún no se han aplicado
    if (prefillData && prefillData.prefill_data && !isPrefilled) {
      const data = prefillData.prefill_data;
      
      // Actualizar datos del formulario
      setFormData(prev => ({
        ...prev,
        contractor: data.contractor || prev.contractor,
        main_insured: data.main_insured || prev.main_insured
      }));
      
      // Marcar como pre-cargado primero
      setIsPrefilled(true);
      
      // Mostrar toast una sola vez
      toast.info(
        `Datos pre-cargados desde el cliente: ${prefillData.client_name}`,
        { duration: 4000 }
      );
    }
  }, [location.state, isPrefilled]);

  const loadData = async () => {
    await fetchCatalogs();
    if (id) {
      await fetchPolicy();
    }
  };

  const fetchCatalogs = async () => {
    try {
      const types = ['insurers', 'policy_types', 'currencies', 'payment_frequencies', 'payment_methods', 'statuses'];
      const results = await Promise.all(
        types.map(type => api.get(`/catalogs/${type}`))
      );
      
      const newCatalogs = {};
      types.forEach((type, index) => {
        newCatalogs[type] = results[index].data;
      });
      
      setCatalogs(newCatalogs);
      
      // NO establecer valores por defecto - dejar que el usuario seleccione
      // Los Select components mostrarán el placeholder correctamente
    } catch (error) {
      console.error('Error loading catalogs:', error);
    }
  };

  const fetchPolicy = async () => {
    try {
      const response = await api.get(`/policies/${id}`);
      const policyData = response.data;
      
      // Preserve all existing data, pero asegurar valores por defecto para objetos opcionales
      setFormData({
        ...policyData,
        policy_number: policyData.policy_number || policyData.policyNumber || '', // FIX CRÍTICO: Mapeo robusto
        // Mapear start_date del backend a emission_date en el frontend
        // Convertir fechas de DD/MM/AAAA a YYYY-MM-DD para los inputs type="date"
        emission_date: convertDDMMYYYYtoYYYYMMDD(policyData.start_date || policyData.emission_date || ''),
        end_date: convertDDMMYYYYtoYYYYMMDD(policyData.end_date || ''),
        contractor: {
          ...policyData.contractor,
          dob: convertDDMMYYYYtoYYYYMMDD(policyData.contractor?.dob || '')
        },
        main_insured: {
          ...policyData.main_insured,
          dob: convertDDMMYYYYtoYYYYMMDD(policyData.main_insured?.dob || '')
        },
        additional_insured: (policyData.additional_insured || []).map(insured => ({
          ...insured,
          dob: convertDDMMYYYYtoYYYYMMDD(insured.dob || '')
        })),
        // NOTA: address fue eliminado del modelo de póliza
        payment: policyData.payment || {
          frequency: '',
          method: '',
          medium: '',
          day: '',
        },
      });
      
      // DETECTAR si el contratante y el asegurado son la misma persona
      const contractorName = policyData.contractor?.name || '';
      const contractorDob = policyData.contractor?.dob || '';
      const insuredName = policyData.main_insured?.name || '';
      const insuredDob = policyData.main_insured?.dob || '';
      
      // Si los nombres y las fechas de nacimiento coinciden, marcar el checkbox
      if (contractorName && insuredName && 
          contractorName === insuredName && 
          contractorDob === insuredDob) {
        setSameAsInsured(true);
      }
      
      // Load plans for this policy - preserve the existing plan value
      if (policyData.insurer && policyData.policy_type) {
        await fetchPlans(policyData.insurer, policyData.policy_type, true);
      }
    } catch (error) {
      toast.error('Error al cargar la póliza');
      navigate('/policies');
    }
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    
    // Fetch plans when insurer or policy_type changes
    if (field === 'insurer' || field === 'policy_type') {
      const insurer = field === 'insurer' ? value : formData.insurer;
      const policyType = field === 'policy_type' ? value : formData.policy_type;
      if (insurer && policyType) {
        fetchPlans(insurer, policyType);
      }
    }
  };

  const fetchPlans = async (insurer, policyType, preservePlan = false) => {
    try {
      const response = await api.get('/plans', {
        params: { insurer, policy_type: policyType }
      });
      setAvailablePlans(response.data);
      
      // Only reset plan if not preserving and not in available list
      if (!preservePlan && response.data.length > 0) {
        setFormData(prev => {
          const planExists = response.data.find(p => p.name === prev.plan);
          return planExists ? prev : { ...prev, plan: '' };
        });
      }
    } catch (error) {
      console.error('Error loading plans:', error);
      setAvailablePlans([]);
    }
  };

  // Efecto para copiar datos del contratante al asegurado principal
  useEffect(() => {
    if (sameAsInsured) {
      setFormData(prev => ({
        ...prev,
        main_insured: {
          name: prev.contractor.name,
          dob: prev.contractor.dob
        }
      }));
    }
  }, [sameAsInsured, formData.contractor.name, formData.contractor.dob]);

  // NOTA: handleZipCodeChange fue eliminado ya que no se usa dirección en pólizas

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // VALIDACIÓN DE CAMPOS OBLIGATORIOS
    const missingFields = [];
    
    if (!formData.policy_number?.trim()) {
      missingFields.push('Número de Póliza');
    }
    if (!formData.contractor?.name?.trim()) {
      missingFields.push('Nombre del Contratante');
    }
    if (!formData.main_insured?.name?.trim()) {
      missingFields.push('Nombre del Asegurado Principal');
    }
    
    // Si faltan campos obligatorios, mostrar error y no enviar
    if (missingFields.length > 0) {
      toast.error(`Por favor completa los siguientes campos obligatorios: ${missingFields.join(', ')}`);
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        ...formData,
        // CONVERTIR FECHAS de YYYY-MM-DD a DD/MM/AAAA para el backend
        // REGLA DE NEGOCIO: start_date = emission_date (para compatibilidad con backend)
        start_date: convertYYYYMMDDtoDDMMYYYY(formData.emission_date),
        end_date: convertYYYYMMDDtoDDMMYYYY(formData.end_date),
        emission_date: convertYYYYMMDDtoDDMMYYYY(formData.emission_date),
        contractor: {
          ...formData.contractor,
          dob: convertYYYYMMDDtoDDMMYYYY(formData.contractor.dob)
        },
        main_insured: {
          ...formData.main_insured,
          dob: convertYYYYMMDDtoDDMMYYYY(formData.main_insured.dob)
        },
        additional_insured: (formData.additional_insured || []).map(insured => ({
          ...insured,
          dob: convertYYYYMMDDtoDDMMYYYY(insured.dob)
        })),
        insured_amount: parseFloat(formData.insured_amount) || 0,
        annual_premium: parseFloat(formData.annual_premium) || 0,
        // NOTA: email, phone y address fueron eliminados del modelo de póliza
        // Enviar payment solo si tiene frecuencia definida
        payment: formData.payment.frequency ? {
          ...formData.payment,
          day: parseInt(formData.payment.day) || 1,
        } : null,
      };

      if (id) {
        await api.put(`/policies/${id}`, payload);
        toast.success('Póliza actualizada exitosamente');
      } else {
        await api.post('/policies', payload);
        toast.success('Póliza creada exitosamente');
      }
      navigate('/policies');
    } catch (error) {
      // Manejar errores de validación del backend
      const errorMessage = error.response?.data?.detail;
      if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else if (Array.isArray(errorMessage)) {
        // Errores de validación de Pydantic
        const errors = errorMessage.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        toast.error(`Error de validación: ${errors}`);
      } else {
        toast.error('Error al guardar la póliza');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Button
        data-testid="back-button"
        variant="ghost"
        onClick={() => navigate('/policies')}
        className="mb-4 text-slate-600 hover:text-[#2537b0] hover:bg-blue-50"
      >
        <ArrowLeft size={20} className="mr-2" />
        Volver
      </Button>

      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {id ? 'Editar Póliza' : 'Nueva Póliza'}
        </h1>
        <p className="text-sm leading-relaxed text-slate-600 mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
          Completa los datos de la póliza
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Information - REORDENADO Y SOLO 3 CAMPOS OBLIGATORIOS */}
        <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6 mb-6">
          <h2 className="text-xl font-medium text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Información Básica
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* OBLIGATORIO 1: Número de Póliza */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Número de Póliza*</Label>
              <Input
                data-testid="policy-number-input"
                value={formData.policy_number}
                onChange={(e) => handleChange('policy_number', e.target.value)}
                required
                className="mt-1"
              />
            </div>
            
            {/* OPCIONAL: Aseguradora (va primero para flujo lógico) */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Aseguradora</Label>
              <Select value={formData.insurer} onValueChange={(value) => handleChange('insurer', value)}>
                <SelectTrigger data-testid="insurer-select" className="mt-1">
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.insurers.map((item) => (
                    <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* OPCIONAL: Tipo (segundo para filtrar planes) */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo</Label>
              <Select value={formData.policy_type} onValueChange={(value) => handleChange('policy_type', value)}>
                <SelectTrigger data-testid="policy-type-select" className="mt-1">
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.policy_types.map((item) => (
                    <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* OPCIONAL: Plan (depende de Aseguradora + Tipo) */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Plan</Label>
              {availablePlans.length > 0 ? (
                <Select value={formData.plan} onValueChange={(value) => handleChange('plan', value)}>
                  <SelectTrigger data-testid="plan-select" className="mt-1">
                    <SelectValue placeholder="Selecciona un plan..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.name}>{plan.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  data-testid="plan-input"
                  value={formData.plan}
                  onChange={(e) => handleChange('plan', e.target.value)}
                  className="mt-1"
                  placeholder="Ingrese el plan o seleccione aseguradora y tipo"
                />
              )}
            </div>
            
            {/* OPCIONAL: Estatus */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Estatus</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>


        {/* Indicador de datos pre-cargados */}
        {isPrefilled && !id && (
          <Alert className="mb-6 bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-900">Datos Pre-cargados</AlertTitle>
            <AlertDescription className="text-blue-800">
              Los datos del contratante han sido completados automáticamente desde el perfil del cliente.
              Puedes modificarlos si es necesario antes de guardar la póliza.
            </AlertDescription>
          </Alert>
        )}

                <SelectTrigger data-testid="status-select" className="mt-1">
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.statuses.map((item) => (
                    <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Dates - EMISIÓN Y VENCIMIENTO */}
        <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6 mb-6">
          <h2 className="text-xl font-medium text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Fechas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Emisión</Label>
                <div className="group relative inline-flex">
                  <Info size={14} className="text-blue-500 cursor-help hover:text-blue-700 transition-colors" />
                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-32 p-2 bg-slate-800 text-white text-xs rounded-md shadow-lg z-10">
                    <strong>Inicia</strong> la vigencia
                  </div>
                </div>
              </div>
              <Input
                data-testid="emission-date-input"
                type="date"
                value={formData.emission_date}
                onChange={(e) => handleChange('emission_date', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vencimiento</Label>
                <div className="group relative inline-flex">
                  <Info size={14} className="text-blue-500 cursor-help hover:text-blue-700 transition-colors" />
                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-32 p-2 bg-slate-800 text-white text-xs rounded-md shadow-lg z-10">
                    <strong>Termina</strong> la vigencia
                  </div>
                </div>
              </div>
              <Input
                data-testid="end-date-input"
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </Card>

        {/* People - OBLIGATORIOS: contractor y main_insured_name */}
        <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6 mb-6">
          <h2 className="text-xl font-medium text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Personas
          </h2>

          {/* CONTRATANTE - PRIMERO */}
          <div className="bg-blue-50 p-4 rounded-[10px] mb-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3">📋 Contratante</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nombre del Contratante*</Label>
                <Input
                  data-testid="contractor-name-input"
                  value={formData.contractor.name}
                  onChange={(e) => handleChange('contractor.name', e.target.value)}
                  required
                  className="mt-1"
                  placeholder="Nombre completo del contratante"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha de Nacimiento (Contratante)</Label>
                <Input
                  data-testid="contractor-dob-input"
                  type="date"
                  value={formData.contractor.dob}
                  onChange={(e) => handleChange('contractor.dob', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* CHECKBOX PARA COPIAR DATOS */}
          <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded-[10px]">
            <input
              type="checkbox"
              id="same-as-insured"
              checked={sameAsInsured}
              onChange={(e) => setSameAsInsured(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <Label htmlFor="same-as-insured" className="text-sm font-medium text-slate-700 cursor-pointer">
              El asegurado principal es la misma persona que el contratante
            </Label>
          </div>

          {/* ASEGURADO PRINCIPAL - Solo si NO es el mismo */}
          {!sameAsInsured && (
            <div className="bg-green-50 p-4 rounded-[10px] mb-4">
              <h3 className="text-sm font-semibold text-green-900 mb-3">🛡️ Asegurado Principal</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nombre del Asegurado Principal*</Label>
                  <Input
                    data-testid="main-insured-name-input"
                    value={formData.main_insured.name}
                    onChange={(e) => handleChange('main_insured.name', e.target.value)}
                    required
                    className="mt-1"
                    placeholder="Nombre completo del asegurado"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha de Nacimiento (Asegurado Principal)</Label>
                  <Input
                    data-testid="main-insured-dob-input"
                    type="date"
                    value={formData.main_insured.dob}
                    onChange={(e) => handleChange('main_insured.dob', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          {sameAsInsured && (
            <div className="bg-green-50 p-3 rounded-[10px] mb-4 text-sm text-green-800">
              ℹ️ El asegurado principal será: <strong>{formData.contractor.name || '(sin nombre)'}</strong>
            </div>
          )}

          {/* Additional Insured */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-slate-800 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Asegurados Adicionales
            </h3>
            <InsuredList 
              insured={formData.additional_insured}
              onChange={(value) => handleChange('additional_insured', value)}
            />
          </div>
        </Card>

        {/* NOTA: Secciones de Contacto y Dirección fueron eliminadas.
            Estos datos se obtienen del cliente vinculado a la póliza */}

        {/* Financial */}
        <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6 mb-6">
          <h2 className="text-xl font-medium text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Información Financiera
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Moneda*</Label>
              <Select 
                value={formData.currency} 
                onValueChange={(value) => handleChange('currency', value)}
              >
                <SelectTrigger data-testid="currency-select" className="mt-1">
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.currencies.map((item) => (
                    <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Suma Asegurada</Label>
              <Input
                data-testid="insured-amount-input"
                type="number"
                step="0.01"
                value={formData.insured_amount}
                onChange={(e) => handleChange('insured_amount', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Prima Anual</Label>
              <Input
                data-testid="annual-premium-input"
                type="number"
                step="0.01"
                value={formData.annual_premium}
                onChange={(e) => handleChange('annual_premium', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2 italic">
            ℹ️ La comisión se calcula automáticamente según la configuración de tipo de póliza, plan y año de vigencia
          </p>
        </Card>

        {/* Payment - TODOS OPCIONALES */}
        <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6 mb-6">
          <h2 className="text-xl font-medium text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Forma de Pago
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Frecuencia de Pago</Label>
              <Select value={formData.payment.frequency} onValueChange={(value) => handleChange('payment.frequency', value)}>
                <SelectTrigger data-testid="payment-frequency-select" className="mt-1">
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.payment_frequencies.map((item) => (
                    <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Método de Pago</Label>
              <Select value={formData.payment.method} onValueChange={(value) => handleChange('payment.method', value)}>
                <SelectTrigger data-testid="payment-method-select" className="mt-1">
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.payment_methods.map((item) => (
                    <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Vehicle Info - SOLO para pólizas de tipo Auto */}
        {formData.policy_type && formData.policy_type.toUpperCase().includes('AUTO') && (
          <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6 mb-6">
            <h2 className="text-xl font-medium text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Datos del Vehículo
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vehículo Asegurado</Label>
                <Input
                  data-testid="vehiculo-asegurado-input"
                  value={formData.vehicle_info.vehiculo_asegurado}
                  onChange={(e) => handleChange('vehicle_info.vehiculo_asegurado', e.target.value)}
                  placeholder="Ej: Honda Civic, Toyota Corolla"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Modelo (Año)</Label>
                <Input
                  data-testid="modelo-input"
                  value={formData.vehicle_info.modelo}
                  onChange={(e) => handleChange('vehicle_info.modelo', e.target.value)}
                  placeholder="Ej: 2023"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Serie / VIN</Label>
                <Input
                  data-testid="serie-input"
                  value={formData.vehicle_info.serie}
                  onChange={(e) => handleChange('vehicle_info.serie', e.target.value)}
                  placeholder="Número de serie del vehículo"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Placa</Label>
                <Input
                  data-testid="placa-input"
                  value={formData.vehicle_info.placa}
                  onChange={(e) => handleChange('vehicle_info.placa', e.target.value)}
                  placeholder="Ej: ABC-123-D"
                  className="mt-1"
                />
              </div>
            </div>
          </Card>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/policies')}
            className="border-[#e0e0e0] text-slate-700 hover:bg-slate-50"
          >
            Cancelar
          </Button>
          <Button
            data-testid="save-policy-button"
            type="submit"
            disabled={loading}
            className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white rounded-[10px] font-medium px-6 py-2.5 transition-all shadow-sm hover:shadow-md"
          >
            <Save size={20} className="mr-2" />
            {loading ? 'Guardando...' : 'Guardar Póliza'}
          </Button>
        </div>
      </form>
    </div>
  );
};
